"use client";

import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import {
	EyeIcon,
	RocketIcon,
	TrendingUpIcon,
} from "@/components/icons/animated-reach-icons";
import { buildHomePricingHref } from "@/lib/pricing-navigation";
import type { PricingRecord } from "@/lib/pricing-record";
import {
	getCloudflareTracePricingCountry,
	normalizePricingCountry,
	type PricingCountry,
} from "@/lib/pricing-region";
import { cn } from "@/lib/utils";

interface PricingApiResponse {
	meta?: {
		country?: string;
	};
	records?: PricingRecord[];
}

interface HeroProps {
	headingVariant?: "default" | "get-views";
}

const PACKAGE_PLANS = [
	{
		id: 14,
		name: "10K views",
		description: "Every breakthrough starts with the right first push.",
		icon: EyeIcon,
		featured: false,
		elevation: "shadow-[0_2px_8px_-6px_rgba(24,24,27,0.16)]",
		hoverElevation:
			"shadow-[0_7px_18px_-12px_rgba(24,24,27,0.24),0_2px_6px_-5px_rgba(24,24,27,0.12)]",
	},
	{
		id: 15,
		name: "20K views",
		description: "Your next release deserves real momentum.",
		icon: TrendingUpIcon,
		featured: true,
		elevation:
			"relative z-10 shadow-[0_18px_40px_-20px_rgba(220,38,38,0.38),0_8px_18px_-14px_rgba(24,24,27,0.22)]",
		hoverElevation:
			"shadow-[0_18px_40px_-20px_rgba(220,38,38,0.38),0_8px_18px_-14px_rgba(24,24,27,0.22)]",
	},
	{
		id: 16,
		name: "50K views",
		description: "When the vision is bigger, the campaign should be too.",
		icon: RocketIcon,
		featured: false,
		elevation:
			"shadow-[0_15px_34px_-18px_rgba(24,24,27,0.36),0_4px_12px_-7px_rgba(24,24,27,0.18)]",
		hoverElevation:
			"shadow-[0_17px_36px_-18px_rgba(24,24,27,0.38),0_5px_12px_-8px_rgba(24,24,27,0.18)]",
	},
] as const;

const PRICE_FORMATTERS = {
	inr: new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency: "INR",
		maximumFractionDigits: 0,
	}),
	usd: new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	}),
} satisfies Record<PricingCountry, Intl.NumberFormat>;

function formatPlanPrice(
	planId: number,
	country: PricingCountry,
	records: PricingRecord[],
) {
	const record = records.find((item) => item.id === planId);
	if (!record) return "—";

	const amount = country === "inr" ? record.price_in_inr : record.price_in_usd;
	return PRICE_FORMATTERS[country].format(amount);
}

export function CreatorProof({ dark = false }: { dark?: boolean }) {
	return (
		<div className="flex flex-wrap items-center gap-3">
			<div className="flex -space-x-2">
				{[
					{
						src: "/logos/clients/sony-entertainment-television.webp",
						alt: "Sony Entertainment Television",
					},
					{
						src: "/logos/clients/t-series-network.jpg",
						alt: "T-Series",
					},
					{
						src: "/logos/clients/zee-music-company-common.webp",
						alt: "Zee Music Company",
					},
				].map(({ src, alt }) => (
					<Image
						key={src}
						src={src}
						alt={alt}
						width={40}
						height={40}
						className="size-10 rounded-full border border-red-600 bg-white object-cover shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
					/>
				))}
			</div>
			<p
				className={cn(
					"font-jost text-sm font-semibold",
					dark ? "text-zinc-300" : "text-zinc-600",
				)}
			>
				<span className={dark ? "text-white" : "text-zinc-950"}>4.9/5</span>{" "}
				from 800+ reviews
			</p>
		</div>
	);
}

export default function Hero({ headingVariant = "default" }: HeroProps) {
	const [activePlanId, setActivePlanId] = useState<number | null>(null);
	const [pricing, setPricing] = useState<{
		country: PricingCountry;
		records: PricingRecord[];
	}>({ country: "usd", records: [] });

	useEffect(() => {
		const controller = new AbortController();

		async function loadPricing() {
			try {
				const response = await fetch("/api/pricing", {
					cache: "no-store",
					headers: { accept: "application/json" },
					signal: controller.signal,
				});
				if (!response.ok) return;

				const payload = (await response.json()) as PricingApiResponse;
				const country =
					(await getCloudflareTracePricingCountry(controller.signal)) ??
					normalizePricingCountry(payload.meta?.country);

				if (Array.isArray(payload.records)) {
					setPricing({ country, records: payload.records });
				}
			} catch {
				// The cards retain a compact unavailable-price fallback.
			}
		}

		loadPricing();
		return () => controller.abort();
	}, []);

	return (
		<section className="bg-zinc-100 pb-16 pt-28 sm:pt-32 lg:pb-24 lg:pt-36">
			<div className="mx-auto max-w-7xl px-4 md:px-6 2xl:max-w-[96rem]">
				<div className="grid gap-8 md:grid-cols-[minmax(0,34rem)_minmax(0,48rem)] md:items-center md:justify-between xl:gap-12">
					<div>
						<p className="font-jost text-sm font-bold uppercase tracking-[0.24em] text-red-700">
							Managed YouTube ad campaigns
						</p>
						<h1
							aria-label={
								headingVariant === "get-views"
									? "Get views and subscribers, watchtime"
									: "Views, subscribers, watchtime"
							}
							className={cn(
								"mt-6 max-w-3xl text-balance font-jost font-black leading-[0.94] tracking-tight text-zinc-950 sm:text-6xl",
								headingVariant === "get-views" ? "text-[2.625rem]" : "text-5xl",
							)}
						>
							<span
								className={cn(
									"block",
									headingVariant === "get-views" && "whitespace-nowrap",
								)}
							>
								{headingVariant === "get-views" ? "Get Views" : "Views"}
							</span>
							<span className="block text-red-600">Subscribers</span>
							<span className="block bg-amber-700 bg-[url(/noise.svg)] bg-[length:56px_56px] bg-clip-text bg-blend-multiply text-transparent">
								Watchtime
							</span>
						</h1>
						<p className="mt-6 max-w-xl text-pretty text-lg leading-8 text-zinc-600">
							We plan and run paid YouTube campaigns that put your videos in
							front of real viewers. Start with a view package or compare every
							plan.
						</p>
						<p className="mt-3 max-w-xl text-sm leading-6 text-zinc-500">
							No bots or fake engagement. Paid campaign watch time and
							subscribers do not count toward YouTube Partner Program
							eligibility.
						</p>
						<div className="mt-8">
							<CreatorProof />
						</div>
					</div>

					<div className="grid w-full gap-4 md:self-center xl:max-w-3xl xl:grid-cols-3 xl:items-center xl:justify-self-end xl:gap-5">
						{PACKAGE_PLANS.map((plan) => (
							<article
								key={plan.id}
								onMouseEnter={() => {
									if (!plan.featured) setActivePlanId(plan.id);
								}}
								onMouseLeave={() => setActivePlanId(null)}
								onFocus={() => {
									if (!plan.featured) setActivePlanId(plan.id);
								}}
								onBlur={() => setActivePlanId(null)}
								className={cn(
									"grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-3xl border p-3 transition-shadow duration-150 ease-out motion-reduce:transition-none xl:flex xl:flex-col xl:items-stretch xl:gap-6 xl:p-6",
									plan.featured ? "xl:min-h-[25rem]" : "xl:min-h-96",
									activePlanId === plan.id
										? plan.hoverElevation
										: plan.elevation,
									activePlanId === plan.id && "relative z-20",
									plan.featured
										? "border-red-600 bg-red-600 text-white"
										: "border-zinc-200 bg-white text-zinc-950",
								)}
							>
								<div className="flex min-w-0 flex-wrap items-center gap-2.5">
									<div
										className={cn(
											"flex size-9 shrink-0 items-center justify-center rounded-xl xl:size-8",
											plan.featured
												? "bg-white/15 text-white"
												: "bg-red-50 text-red-600",
										)}
									>
										<plan.icon size={20} aria-hidden="true" />
									</div>
									<h2 className="whitespace-nowrap font-jost text-2xl font-black lg:text-3xl xl:text-2xl">
										{plan.name}
									</h2>
								</div>

								<p
									className={cn(
										"hidden text-balance font-jost font-bold leading-snug tracking-[0.015em] xl:block",
										plan.featured
											? "text-lg text-white/85"
											: "text-base text-zinc-700/80",
									)}
								>
									{plan.description}
								</p>

								<div className="md:text-end xl:mt-auto xl:text-start">
									<p className="sr-only">Starting at</p>
									<p
										aria-live="polite"
										className="whitespace-nowrap font-jost text-3xl font-black tabular-nums sm:text-4xl"
									>
										{pricing.records.some((item) => item.id === plan.id) ? (
											formatPlanPrice(plan.id, pricing.country, pricing.records)
										) : (
											<>
												<span aria-hidden="true">—</span>
												<span className="sr-only">
													Price loading or unavailable
												</span>
											</>
										)}
									</p>
								</div>

								<Link
									href={buildHomePricingHref({
										tab: "reach",
										planId: plan.id,
									})}
									aria-label={`Pay now for ${plan.name}`}
									prefetch={false}
									data-analytics-event="select_promotion"
									data-analytics-label={`hero_${plan.name.toLowerCase().replaceAll(" ", "_")}`}
									className={cn(
										"group relative col-span-2 mt-1 inline-flex min-h-11 items-center justify-center overflow-hidden rounded-2xl px-4 font-jost text-sm font-bold uppercase tracking-wide transition-[background-color,color,scale] duration-[180ms] ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-4 xl:col-span-1 xl:mt-0 xl:w-full",
										plan.featured
											? "bg-white text-zinc-950 hover:text-white focus-visible:text-white focus-visible:outline-white"
											: "bg-zinc-950 text-white hover:bg-red-600 focus-visible:outline-zinc-950",
									)}
								>
									{plan.featured && (
										<span
											aria-hidden="true"
											data-cta-fill
											className="pointer-events-none absolute inset-0 grid grid-rows-3"
										>
											<span className="overflow-hidden">
												<span
													data-cta-fill-panel
													className="block size-full translate-y-full bg-zinc-950 transition-transform delay-75 duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-focus-visible:translate-y-0 motion-reduce:transition-none"
												/>
											</span>
											<span className="overflow-hidden">
												<span
													data-cta-fill-panel
													className="block size-full translate-y-full bg-zinc-950 transition-transform delay-[35ms] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-focus-visible:translate-y-0 motion-reduce:transition-none"
												/>
											</span>
											<span className="overflow-hidden">
												<span
													data-cta-fill-panel
													className="block size-full translate-y-full bg-zinc-950 transition-transform duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-focus-visible:translate-y-0 motion-reduce:transition-none"
												/>
											</span>
										</span>
									)}
									<span className="relative z-10">Pay now</span>
									<ArrowRight
										className="relative z-10 ms-2 size-4 transition-transform duration-[180ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 motion-reduce:transition-none"
										aria-hidden="true"
									/>
								</Link>
							</article>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
